// Hotjar tracking integration for production only.
//
// ⚠️ Gated on NEXT_PUBLIC_RELEASE_STAGE, never on NODE_ENV. `next build` sets
// NODE_ENV=production at runtime in BOTH deployments, so NODE_ENV cannot tell
// app-test from app — this file used to gate on it and was therefore recording
// app-test.gobo.show sessions into the production Hotjar site, mixing QA runs
// into real user recordings and heatmaps. apphosting.yaml sets "production",
// apphosting.test.yaml sets "test". Bugsnag, the Slack notifications and
// lib/ga.ts all use the same discriminator. See docs/analytics.md.
//
// Local development sets no RELEASE_STAGE, so it stays off there too, which is
// the behaviour the old NODE_ENV check was reaching for.
declare global {
  interface Window {
    hj?: {
      (...args: unknown[]): void;
      q?: unknown[][];
    };
    _hjSettings?: {
      hjid: number;
      hjsv: number;
    };
  }
}

interface HotjarWindow extends Window {
  hj: {
    (...args: unknown[]): void;
    q?: unknown[][];
  };
  _hjSettings: {
    hjid: number;
    hjsv: number;
  };
}

/** True only on the production deployment. */
const isProductionStage = (): boolean =>
  process.env.NEXT_PUBLIC_RELEASE_STAGE === 'production';

export const initHotjar = (): void => {
  // Only load Hotjar on the production deployment — see the note above.
  if (!isProductionStage()) {
    return;
  }

  // Only load on client side
  if (typeof window === 'undefined') {
    return;
  }

  // Check if already loaded
  if (window.hj) {
    return;
  }

  // Get Hotjar configuration from environment variables
  const hjid = parseInt(process.env.NEXT_PUBLIC_HOTJAR_ID || '6580974', 10);
  const hjsv = parseInt(process.env.NEXT_PUBLIC_HOTJAR_SV || '6', 10);

  // Hotjar tracking code for Gobo Pro
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=${hjsv}`;
  
  // Initialize Hotjar function and settings
  (window as HotjarWindow).hj = (window as HotjarWindow).hj || (function(...args: unknown[]) {
    ((window as HotjarWindow).hj.q = (window as HotjarWindow).hj.q || []).push(args);
  } as HotjarWindow['hj']);
  (window as HotjarWindow)._hjSettings = { hjid: hjid, hjsv: hjsv };
  
  document.head.appendChild(script);
};

// Helper function to track events (optional)
export const hotjarEvent = (eventName: string): void => {
  if (typeof window !== 'undefined' && window.hj && isProductionStage()) {
    window.hj('event', eventName);
  }
};

const hotjarModule = { initHotjar, hotjarEvent };
export default hotjarModule;